/* Kasa · "Mi suscripción"
 * -------------------------
 * Misma mecánica que el bloque del PDP (tamaño -> uno o dos -> cafés con su
 * molienda), pero en vez de añadir al carrito modifica el contrato vía el App
 * Proxy. Se entra viendo lo que ya se tiene; editar es una decisión explícita
 * y arranca con todo preseleccionado.
 */

(function () {
  'use strict';

  class KasaMiSuscripcion extends HTMLElement {
    connectedCallback() {
      this.api = this.dataset.api || '';
      this.variants = this.#json(this.dataset.variants) || [];
      this.coffeeMap = this.#json(this.dataset.coffees) || [];
      this.grindOptions = (this.dataset.grindOptions || '').split(',').map((x) => x.trim()).filter(Boolean);
      this.grindLabel = this.dataset.grindLabel || 'Molienda';
      this.i18n = {
        grindHelp: this.dataset.i18nGrindHelp || '',
        needOne: this.dataset.i18nNeedOne || '',
        needTwo: this.dataset.i18nNeedTwo || '',
        split: this.dataset.i18nSplit || '',
        chooseOne: this.dataset.i18nChooseOne || 'elige 1',
        chooseTwo: this.dataset.i18nChooseTwo || 'elige 2',
        keepTag: this.dataset.i18nKeepTag || 'Tu café',
        newTag: this.dataset.i18nNewTag || 'El nuevo',
        saving: this.dataset.i18nSaving || 'Guardando…',
        saveError: this.dataset.i18nSaveError || 'No hemos podido guardarlo. Inténtalo de nuevo.',
        saveConflict: this.dataset.i18nSaveErrorConflict
          || 'Tu suscripción ha cambiado mientras editabas. Recarga la página para ver cómo está ahora.',
      };

      this.refs = {
        loading: this.querySelector('[data-loading]'),
        none: this.querySelector('[data-none]'),
        error: this.querySelector('[data-error]'),
        main: this.querySelector('[data-main]'),
        resume: this.querySelector('[data-resume]'),
        resumeList: this.querySelector('[data-resume-list]'),
        editor: this.querySelector('[data-editor]'),
        ok: this.querySelector('[data-ok]'),
        sizes: this.querySelector('[data-sizes]'),
        sizeStep: this.querySelector('[data-size-step]'),
        modeStep: this.querySelector('[data-mode-step]'),
        modeNote: this.querySelector('[data-mode-note]'),
        counter: this.querySelector('[data-counter]'),
        summary: this.querySelector('[data-summary]'),
        hint: this.querySelector('[data-hint]'),
        submit: this.querySelector('[data-submit]'),
        modeOpts: this.querySelector('[data-mode-opts]'),
        cards: this.querySelector('[data-cards]'),
        grid: this.querySelector('[data-grid]'),
        allBtn: this.querySelector('[data-all]'),
        freqStep: this.querySelector('[data-freq-step]'),
        freqs: this.querySelector('[data-freqs]'),
      };

      // "Etiqueta = valor" por línea; el valor es el que entiende Seal
      this.frequencies = (this.dataset.frequencies || '')
        .split('\n')
        .map((line) => {
          const [label, value] = line.split('=').map((x) => x.trim());
          return value ? { label, value } : null;
        })
        .filter(Boolean);
      // Candidatos a muestra, en orden. A cada cliente le toca el primero que
      // aún no tiene: la mostra debe ser algo nuevo para quien la recibe.
      this.samples = this.#json(this.dataset.samples) || [];

      this.modeInputs = Array.from(this.querySelectorAll('[data-mode]'));
      this.coffeeInputs = Array.from(this.querySelectorAll('[data-coffee-input]'));
      this.selection = [];
      this.grinds = {};
      this.variantId = null;

      // Cada paso se desbloquea por separado: el café ya viene abierto
      this.querySelectorAll('[data-unlock]').forEach((btn) =>
        btn.addEventListener('click', () => this.#unlock(btn.dataset.unlock))
      );
      this.refs.allBtn?.addEventListener('click', () => this.#showAll());
      this.querySelectorAll('[data-quick]').forEach((el) =>
        el.addEventListener('change', () => this.#quickPick(el.dataset.quick, el))
      );
      this.refs.submit?.addEventListener('click', () => this.#save());
      this.querySelector('[data-retry]')?.addEventListener('click', () => this.#retry());
      this.querySelector('[data-edit-again]')?.addEventListener('click', () => this.#editAgain());
      this.modeInputs.forEach((el) => el.addEventListener('change', () => this.#applyMode()));
      this.coffeeInputs.forEach((el) => el.addEventListener('change', () => this.#toggleCoffee(el)));

      this.#load();
    }

    #json(str) { try { return JSON.parse(str); } catch (_) { return null; } }

    /**
     * Enseña un bloque con una entrada corta en vez de un salto seco.
     *
     * El reflow forzado es necesario: sin él el navegador agrupa el quitar
     * `hidden` y el añadir la clase en el mismo frame, y la animación no llega
     * a arrancar. Quien tenga activado "reducir movimiento" no ve nada de
     * esto: lo apaga el CSS, no el JS.
     */
    #reveal(el) {
      if (!el) return;
      el.classList.remove('kxm--in');
      el.hidden = false;
      void el.offsetWidth;
      el.classList.add('kxm--in');
    }

    /**
     * Qué muestra le toca a este cliente.
     *
     * Si el backend ya se la asignó, esa manda: es la que de verdad va en su
     * caja. Solo si aún no está asignada la deducimos del orden de la
     * colección, con la misma regla, para no dejar la ficha vacía.
     */
    get featured() { return this.sampleTitle || ''; }

    /**
     * Qué va en la ficha de la derecha.
     *
     * Normalmente la muestra. Pero si el cliente ya lleva todos los cafés de la
     * colección no queda ninguna que sugerir, y entonces la ficha se quedaba
     * vacía: en dúo eso dejaba uno de sus dos cafés sin representar arriba,
     * visible solo en su fila de molienda. Así que en ese caso la ficha pasa a
     * ser su segundo café.
     *
     * Como `featured`, se congela al cargar: si dependiera de la selección viva
     * volvería el problema de la ficha que se desmarca sola.
     */
    get second() { return this.secondTitle || ''; }

    /** ¿Lo de la derecha es una sugerencia o un café que ya lleva? */
    get secondIsSample() { return Boolean(this.sampleTitle) && this.second === this.sampleTitle; }

    /**
     * Se calcula UNA vez, al cargar el contrato, y no se vuelve a tocar.
     *
     * Antes era un getter que miraba `this.selection`, y eso lo hacía cambiar
     * de valor en el momento en que lo seleccionabas: al marcar la ficha, ese
     * café pasaba a estar "en la selección", el getter saltaba al siguiente
     * candidato, y `#syncCards` desmarcaba la ficha por no encontrar el nuevo
     * valor en la selección. Resultado: la ficha no se veía marcada aunque la
     * molienda de abajo sí cambiaba, y al segundo clic parecía funcionar
     * -pero solo porque el checkbox se quedaba marcado por su cuenta.
     *
     * La muestra depende del contrato, no de lo que estés pulsando ahora
     * mismo, así que congelarla es además lo correcto.
     */
    #resolveSample() {
      if (this.assignedSample) return this.assignedSample;
      const contratados = [this.subscription?.coffee1, this.subscription?.coffee2]
        .filter(Boolean)
        .map((t) => String(t).toLowerCase());
      const hit = this.samples.find((s) => !contratados.includes(String(s.title).toLowerCase()));
      return hit ? hit.title : '';
    }

    /** La imagen puede venir de la colección de muestras o de la de cafés. */
    #imageFor(title) {
      const want = String(title || '').toLowerCase();
      if (!want) return null;
      const hit = this.samples.find((x) => String(x.title).toLowerCase() === want)
        || this.coffeeMap.find((x) => String(x.title).toLowerCase() === want);
      return hit ? hit.image : null;
    }

    // ---------- Contrato ----------
    async #load() {
      const url = this.dataset.email
        ? `${this.api}?email=${encodeURIComponent(this.dataset.email)}`
        : this.api;

      let res;
      try {
        res = await fetch(url, { headers: { Accept: 'application/json' } });
      } catch (_) {
        return this.#fail();
      }

      if (res.status === 401) {
        const body = await res.json().catch(() => ({}));
        if (body.error === 'not_logged_in') {
          const back = encodeURIComponent(location.pathname + location.search);
          location.replace(`${this.dataset.login || '/account/login'}?return_url=${back}`);
          return;
        }
        return this.#fail();
      }
      if (!res.ok) return this.#fail();

      const { subscription } = await res.json().catch(() => ({}));
      if (this.refs.loading) this.refs.loading.hidden = true;
      if (this.refs.error) this.refs.error.hidden = true;

      if (!subscription) {
        this.#reveal(this.refs.none);
        return;
      }

      this.subscription = subscription;
      this.variantId = subscription.variantId;
      this.originalCoffee = subscription.coffee1 || null;
      this.interval = subscription.interval || null;
      this.assignedSample = subscription.sample || null;
      this.sampleTitle = this.#resolveSample();
      this.secondTitle = this.sampleTitle
        || (subscription.coffee2 && subscription.coffee2 !== subscription.coffee1 ? subscription.coffee2 : '');
      this.selection = [subscription.coffee1, subscription.coffee2].filter(Boolean);
      if (subscription.coffee1) this.grinds[subscription.coffee1] = subscription.grind1 || this.grindOptions[0] || '';
      if (subscription.coffee2) this.grinds[subscription.coffee2] = subscription.grind2 || this.grindOptions[0] || '';

      const duo = Boolean(subscription.coffee2);
      const target = this.modeInputs.find((m) => m.value === (duo ? 'duo' : 'mono'));
      if (target) target.checked = true;

      this.#reveal(this.refs.main);

      // El formulario se ve de entrada; los candados solo tapan tamaño y modo
      this.#buildSizes();
      this.#syncCoffeeInputs();
      this.#applyMode();
      this.#renderCards();
      this.#buildFreqs();
      this.#renderLocks();
    }

    /** Muestra el valor vigente de los pasos bloqueados. */
    #renderLocks() {
      const size = this.currentVariant;
      const sizeVal = this.querySelector('[data-lock-val="size"]');
      if (sizeVal && size) sizeVal.textContent = size.title;

      const freqVal = this.querySelector('[data-lock-val="freq"]');
      if (freqVal) {
        const hit = this.frequencies.find((f) => f.value === this.interval);
        freqVal.textContent = hit ? hit.label : this.interval || '';
      }

      const modeVal = this.querySelector('[data-lock-val="mode"]');
      if (modeVal) {
        modeVal.textContent = this.isDuo
          ? (this.dataset.i18nModeDuo || 'Dos cafés')
          : (this.dataset.i18nModeMono || 'Un café');
      }
    }

    #unlock(which) {
      const lock = this.querySelector(`[data-lock="${which}"]`);
      if (lock) lock.hidden = true;
      if (which === 'size' && this.refs.sizes) this.refs.sizes.hidden = false;
      if (which === 'freq' && this.refs.freqs) this.refs.freqs.hidden = false;
      if (which === 'mode') {
        if (this.refs.modeOpts) this.refs.modeOpts.hidden = false;
        if (this.refs.modeNote) this.refs.modeNote.hidden = false;
      }
    }

    /** Ficha izquierda = lo que ya tienes; derecha = el de la muestra. */
    #renderCards() {
      const name = this.querySelector('[data-keep-name]');
      const img = this.querySelector('[data-keep-img]');
      const ph = this.querySelector('[data-keep-ph]');
      const current = this.originalCoffee;
      if (!current) { if (this.refs.cards) this.refs.cards.hidden = true; return; }

      if (name) name.textContent = current;
      const src0 = this.#imageFor(current);
      if (img && src0) {
        img.src = src0;
        img.alt = current;
        img.hidden = false;
        if (ph) ph.hidden = true;
      }
      // La ficha derecha muestra la que le toca a este cliente. Solo se cae si
      // ya los tiene todos, y entonces la elección va por la cuadrícula.
      const nu = this.querySelector('[data-quick="new"]');
      const otro = this.second;
      const newCard = nu?.closest('.kxm__card');
      if (newCard) newCard.hidden = !otro;
      this.refs.cards?.classList.toggle('kxm__cards--single', !otro);

      if (otro) {
        const nName = this.querySelector('[data-new-name]');
        const nImg = this.querySelector('[data-new-img]');
        const nPh = this.querySelector('[data-new-ph]');
        const nTag = this.querySelector('[data-new-tag]');
        if (nName) nName.textContent = otro;
        // El rótulo tiene que decir la verdad: "El nuevo" solo si lo es
        if (nTag) {
          nTag.textContent = this.secondIsSample ? this.i18n.newTag : this.i18n.keepTag;
          nTag.classList.toggle('kxm__card-tag--new', this.secondIsSample);
        }
        const src = this.#imageFor(otro);
        if (nImg && src) {
          nImg.src = src;
          nImg.alt = otro;
          nImg.hidden = false;
          if (nPh) nPh.hidden = true;
        }
      }

      const keep = this.querySelector('[data-quick="keep"]');
      if (keep) keep.checked = true;
    }

    #quickPick(which, input) {
      const title = which === 'keep' ? this.originalCoffee : this.second;
      if (!title) return;

      // Con un café la ficha manda sola; con dos, cada una entra y sale
      if (!this.isDuo) return this.#setSelection([title]);

      const without = this.selection.filter((t) => t.toLowerCase() !== title.toLowerCase());
      const next = input.checked ? [...without, title] : without;
      while (next.length > 2) next.shift();
      this.#setSelection(next);
    }

    /** Punto único de cambio: cuadrícula y fichas acaban aquí. */
    #setSelection(titles) {
      const wanted = titles
        .map((t) => this.coffeeInputs.find((c) => c.value.toLowerCase() === String(t).toLowerCase()))
        .filter(Boolean);
      this.coffeeInputs.forEach((c) => (c.checked = wanted.includes(c)));
      this.selection = wanted.map((c) => c.value);
      this.selection.forEach((t) => {
        if (!this.grinds[t]) this.grinds[t] = this.grindOptions[0] || '';
      });
      this.#syncCards();
      this.#render();
    }

    /** Las fichas reflejan lo elegido, venga de donde venga. */
    #syncCards() {
      const keep = this.querySelector('[data-quick="keep"]');
      const nu = this.querySelector('[data-quick="new"]');
      const indexOf = (t) =>
        t ? this.selection.findIndex((s) => s.toLowerCase() === String(t).toLowerCase()) : -1;

      [[keep, this.originalCoffee], [nu, this.second]].forEach(([input, title]) => {
        if (!input) return;
        const idx = indexOf(title);
        input.checked = idx > -1;

        // El orden 1/2 se muestra igual que en la cuadrícula
        const card = input.closest('.kxm__card');
        const badge = card?.querySelector('[data-card-order]');
        if (idx > -1 && this.isDuo) {
          card.setAttribute('data-show-order', '');
          if (badge) badge.textContent = String(idx + 1);
        } else {
          card?.removeAttribute('data-show-order');
          if (badge) badge.textContent = '';
        }
      });
    }

    /** La cuadrícula completa solo si las dos fichas no bastan. */
    #showAll(force) {
      if (!this.refs.grid) return;
      const open = force === undefined ? this.refs.grid.hidden : force;
      this.refs.grid.hidden = !open;
      if (this.refs.allBtn) {
        this.refs.allBtn.textContent = open
          ? (this.dataset.i18nAllClose || 'Ocultar')
          : (this.dataset.i18nAll || 'Ver todos los cafés');
      }
      if (open) this.refs.grid.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /**
     * No podemos afirmar nada del contrato: no enseñamos un editor que
     * guardaría sobre datos que no conocemos.
     *
     * Y sobre todo NO decimos "no tienes suscripción": un fallo de red o un
     * 502 no son una respuesta sobre el contrato, y a quien sí la tiene ese
     * mensaje le hace pensar que la ha perdido.
     */
    #fail() {
      if (this.refs.loading) this.refs.loading.hidden = true;
      if (this.refs.main) this.refs.main.hidden = true;
      if (this.refs.none) this.refs.none.hidden = true;
      this.#reveal(this.refs.error);
    }

    #retry() {
      if (this.refs.error) this.refs.error.hidden = true;
      if (this.refs.loading) this.refs.loading.hidden = false;
      this.#load();
    }

    /** Volver del "¡Listo!" al formulario, sin recargar. */
    #editAgain() {
      if (this.refs.ok) this.refs.ok.hidden = true;
      this.#reveal(this.refs.editor);
      if (this.refs.submit) {
        this.refs.submit.disabled = false;
        this.refs.submit.classList.remove('is-busy');
      }
      if (this.refs.hint) this.refs.hint.textContent = '';
      this.refs.editor?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    #renderResume() {
      const list = this.refs.resumeList;
      if (!list) return;
      const size = this.variants.find((v) => String(v.id) === String(this.variantId));
      const rows = [];
      if (size) rows.push([this.querySelector('[data-size-step] .kxc__legend')?.textContent.trim() || 'Tamaño', size.title]);
      this.selection.forEach((title) => rows.push([title, this.grinds[title] || '']));

      list.innerHTML = '';
      rows.forEach(([k, v]) => {
        const row = document.createElement('div');
        row.className = 'kxm__resume-row';
        const dt = document.createElement('dt');
        dt.className = 'kxm__resume-key';
        dt.textContent = k;
        const dd = document.createElement('dd');
        dd.className = 'kxm__resume-val';
        dd.textContent = v;
        row.append(dt, dd);
        list.appendChild(row);
      });
    }

    // ---------- Editor ----------
    #openEditor() {
      if (this.refs.resume) this.refs.resume.hidden = true;
      if (this.refs.editor) this.refs.editor.hidden = false;
      this.#buildSizes();
      this.#syncCoffeeInputs();
      this.#applyMode();
      this.refs.editor?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    #closeEditor() {
      if (this.refs.editor) this.refs.editor.hidden = true;
      if (this.refs.resume) this.refs.resume.hidden = false;
    }

    /** Marca en la cuadrícula lo que ya se tiene contratado. */
    #syncCoffeeInputs() {
      this.coffeeInputs.forEach((el) => {
        el.checked = this.selection.some((t) => t.toLowerCase() === el.value.toLowerCase());
      });
      // Normalizamos a los títulos reales de la colección
      this.selection = this.coffeeInputs.filter((el) => el.checked).map((el) => el.value);
    }

    #buildSizes() {
      const box = this.refs.sizes;
      if (!box || box.childElementCount || this.variants.length < 2) {
        if (this.variants.length >= 2 && this.refs.sizeStep) this.refs.sizeStep.hidden = false;
        this.#renumber();
        return;
      }
      if (this.refs.sizeStep) this.refs.sizeStep.hidden = false;
      this.variants.forEach((v) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kxc__opt';
        btn.dataset.variantId = String(v.id);
        const label = document.createElement('span');
        label.className = 'kxc__opt-label';
        label.textContent = v.title;
        const sub = document.createElement('span');
        sub.className = 'kxc__opt-sub';
        sub.textContent = v.price;
        btn.append(label, sub);
        btn.addEventListener('click', () => {
          this.variantId = v.id;
          this.#reflectSizes();
          this.#applyMode();
          this.#renderLocks();
        });
        box.appendChild(btn);
      });
      if (this.refs.sizeStep) this.refs.sizeStep.hidden = false;
      this.#reflectSizes();
      this.#renumber();
    }

    #buildFreqs() {
      const box = this.refs.freqs;
      if (!box || box.childElementCount || this.frequencies.length < 2) return;
      this.frequencies.forEach((f) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kxc__opt';
        btn.dataset.interval = f.value;
        const label = document.createElement('span');
        label.className = 'kxc__opt-label';
        label.textContent = f.label;
        btn.appendChild(label);
        btn.addEventListener('click', () => {
          this.interval = f.value;
          this.#reflectFreqs();
          this.#renderLocks();
        });
        box.appendChild(btn);
      });
      if (this.refs.freqStep) this.refs.freqStep.hidden = false;
      this.#reflectFreqs();
      this.#renumber();
    }

    #reflectFreqs() {
      this.refs.freqs?.querySelectorAll('[data-interval]').forEach((b) =>
        b.setAttribute('aria-pressed', b.dataset.interval === this.interval ? 'true' : 'false')
      );
    }

    #reflectSizes() {
      this.refs.sizes?.querySelectorAll('[data-variant-id]').forEach((b) =>
        b.setAttribute('aria-pressed', b.dataset.variantId === String(this.variantId) ? 'true' : 'false')
      );
    }

    get currentVariant() {
      return this.variants.find((v) => String(v.id) === String(this.variantId)) || null;
    }

    get supportsDuo() {
      const v = this.currentVariant;
      return v ? v.duo !== false : true;
    }

    get isDuo() {
      if (!this.supportsDuo) return false;
      const el = this.modeInputs.find((m) => m.checked);
      return !!el && el.value === 'duo';
    }

    get requiredCount() { return this.isDuo ? 2 : 1; }

    #applyMode() {
      if (this.refs.modeStep) this.refs.modeStep.hidden = !this.supportsDuo;
      if (!this.supportsDuo) {
        const mono = this.modeInputs.find((m) => m.value === 'mono');
        if (mono) mono.checked = true;
      }

      const max = this.requiredCount;
      if (this.selection.length > max) {
        this.selection.slice(max).forEach((t) => {
          const el = this.coffeeInputs.find((c) => c.value === t);
          if (el) el.checked = false;
        });
        this.selection = this.selection.slice(0, max);
      }
      // Las fichas valen también en dúo: son "el de siempre + el nuevo"
      if (this.refs.cards) this.refs.cards.hidden = false;
      const pair = [this.originalCoffee, this.second].filter(Boolean);
      const distinct = [...new Set(pair.map((t) => t.toLowerCase()))].length === 2;
      if (this.isDuo && this.selection.length < 2) {
        if (distinct) this.#setSelection(pair);
        else this.#showAll(true); // sin pareja evidente, que elija
      }
      this.#syncCards();

      this.#updateSplit();
      this.#render();
      this.#renumber();
      this.#renderLocks();
    }

    #toggleCoffee(el) {
      const max = this.requiredCount;
      let next = el.checked
        ? [...this.selection.filter((t) => t !== el.value), el.value]
        : this.selection.filter((t) => t !== el.value);
      while (next.length > max) next.shift();
      this.#setSelection(next);
    }

    // ---------- Pintado ----------
    #render() {
      const max = this.requiredCount;
      this.coffeeInputs.forEach((el) => {
        const card = el.closest('[data-coffee]');
        if (!card) return;
        const idx = this.selection.indexOf(el.value);
        const order = card.querySelector('[data-order]');
        if (idx > -1 && this.isDuo) {
          card.setAttribute('data-kxc-show-order', '');
          if (order) order.textContent = String(idx + 1);
        } else {
          card.removeAttribute('data-kxc-show-order');
          if (order) order.textContent = '';
        }
        const full = this.selection.length >= max;
        if (full && idx === -1) card.setAttribute('data-kxc-disabled', '');
        else card.removeAttribute('data-kxc-disabled');
      });

      this.#renderGrinds();
      if (this.refs.counter) {
        this.refs.counter.textContent = max === 1
          ? `· ${this.i18n.chooseOne}`
          : `· ${this.i18n.chooseTwo} (${this.selection.length}/2)`;
      }
      const ok = this.selection.length >= max;
      if (this.refs.submit) this.refs.submit.disabled = !ok;
      if (this.refs.hint) this.refs.hint.textContent = ok ? '' : (max === 2 ? this.i18n.needTwo : this.i18n.needOne);
    }

    /** Una fila por café elegido, con su molienda. */
    #renderGrinds() {
      const box = this.refs.summary;
      if (!box) return;
      if (!this.selection.length) { box.hidden = true; box.innerHTML = ''; return; }
      box.hidden = false;
      box.innerHTML = '';

      this.selection.forEach((title, i) => {
        const row = document.createElement('div');
        row.className = 'kxc__row';
        const main = document.createElement('div');
        main.className = 'kxc__row-main';
        const head = document.createElement('div');
        head.className = 'kxc__row-head';

        if (this.isDuo) {
          const badge = document.createElement('span');
          badge.className = 'kxc__row-badge';
          badge.textContent = String(i + 1);
          head.appendChild(badge);
        }
        const name = document.createElement('span');
        name.className = 'kxc__row-name';
        name.textContent = title;
        head.appendChild(name);

        if (this.i18n.grindHelp) {
          const help = document.createElement('span');
          help.className = 'kxc__row-help';
          help.textContent = this.i18n.grindHelp;
          head.appendChild(help);
        }

        const select = document.createElement('select');
        select.className = 'kxc__grind';
        select.setAttribute('aria-label', `${this.grindLabel} · ${title}`);
        // La molienda guardada puede no estar en esta lista: el PDP y esta
        // página se configuran por separado (y por idioma), así que "Filtro" o
        // "Cold Brew" pueden no tener equivalente aquí. Sin esto el desplegable
        // se quedaba en la primera opción mientras el valor guardado seguía
        // siendo otro: enseñaba una cosa y guardaba otra distinta.
        const saved = this.grinds[title];
        const options = saved && !this.grindOptions.some((o) => o === saved)
          ? [saved, ...this.grindOptions]
          : this.grindOptions;
        options.forEach((opt) => {
          const o = document.createElement('option');
          o.value = opt;
          o.textContent = opt;
          if (opt === this.grinds[title]) o.selected = true;
          select.appendChild(o);
        });
        select.addEventListener('change', () => { this.grinds[title] = select.value; });

        main.append(head, select);
        row.appendChild(main);
        box.appendChild(row);
      });
    }

    /** El dúo reparte el mismo peso, no lo duplica. */
    #updateSplit() {
      const subs = Array.from(this.querySelectorAll('[data-mode-sub]'));
      const v = this.currentVariant;
      const total = v ? this.#grams(v.title) : null;
      if (!total) { subs.forEach((s) => (s.textContent = '')); return; }
      const half = this.#fmt(total / 2);
      const whole = this.#fmt(total);
      const texts = [`1 × ${whole}`, `2 × ${half} = ${whole}`];
      subs.forEach((s, i) => (s.textContent = texts[i] || ''));
      if (this.refs.modeNote) {
        // Se aceptan las dos formas: el editor de temas trata {{ }} como fuente
        // dinámica y lo marca como error, así que los ajustes usan [ ]
        this.refs.modeNote.textContent = (this.i18n.split || '')
          .replace(/\[total\]|\{\{\s*total\s*\}\}/g, whole)
          .replace(/\[half\]|\{\{\s*half\s*\}\}/g, half);
      }
    }

    #grams(title) {
      const m = String(title || '').match(/([\d]+(?:[.,][\d]+)?)\s*(kg|g)\b/i);
      if (!m) return null;
      const n = parseFloat(m[1].replace(',', '.'));
      return Number.isFinite(n) ? (m[2].toLowerCase() === 'kg' ? Math.round(n * 1000) : Math.round(n)) : null;
    }

    #fmt(g) { return g >= 1000 && g % 1000 === 0 ? `${g / 1000}kg` : `${g}g`; }

    #renumber() {
      let n = 0;
      this.querySelectorAll('[data-step]').forEach((step) => {
        const num = step.querySelector('.kxc__num');
        if (!num) return;
        if (step.hidden) { num.textContent = ''; return; }
        n += 1;
        num.textContent = String(n);
      });
    }

    // ---------- Guardar ----------
    async #save() {
      // Sin esto el botón se quedaba mudo entre la pulsación y la respuesta, que
      // con Seal de por medio puede ser un par de segundos largos.
      const btn = this.refs.submit;
      const etiqueta = btn?.textContent;
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-busy');
        btn.textContent = this.i18n.saving;
      }
      if (this.refs.hint) this.refs.hint.textContent = '';
      const [one, two] = this.selection;
      try {
        const res = await fetch(this.api, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscriptionId: this.subscription.id,
            coffee1: one,
            grind1: this.grinds[one] || '',
            coffee2: this.isDuo ? two : null,
            grind2: this.isDuo ? this.grinds[two] || '' : null,
            variantId: this.variantId,
            productHandle: this.dataset.handle,
            localeRoot: this.dataset.localeRoot || '',
            labels: {
              coffee: this.dataset.propCoffee || '',
              coffee1: this.dataset.propCoffee1 || '',
              coffee2: this.dataset.propCoffee2 || '',
            },
            interval: this.interval,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw Object.assign(new Error(String(res.status)), { code: body.error });
        }
        if (this.refs.editor) this.refs.editor.hidden = true;
        this.#reveal(this.refs.ok);
        this.refs.ok?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (err) {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-busy');
          btn.textContent = etiqueta;
        }
        // Reintentar no arregla que el contrato ya no sea el que teníamos
        // delante, ni que el café haya dejado de existir: en esos casos lo
        // honesto es pedir que recargue, no invitarle a repetir lo mismo.
        const stale = ['not_your_subscription', 'no_items', 'unknown_coffee', 'unknown_variant'];
        if (this.refs.hint) {
          this.refs.hint.textContent = stale.includes(err?.code)
            ? this.i18n.saveConflict
            : this.i18n.saveError;
        }
      }
    }
  }

  if (!customElements.get('kasa-mi-suscripcion')) {
    customElements.define('kasa-mi-suscripcion', KasaMiSuscripcion);
  }
})();
